// Ported from EnterpriseTemplates react-project-template (Release 1.3.1), PMS slice 4b.
// ============================================================================
// ORGANISATION (ORG ADMIN) TYPES
// ============================================================================
// The tenant's entities, business units and financial years as this
// application projects them from the Tenant Hub organisation master
// (TENANT_ONBOARDING_AND_SETUP_STANDARD.md §7.5, D-5). Matches
// api-project-template's ProjectedEntity / ProjectedBusinessUnit /
// ProjectedFinancialYear and the OrgAdminController at /api/v1/org/*.
//
// `id` is the LOCAL key (used in every header and foreign key of this app);
// `canonicalId` is the master's id and is what crosses application boundaries.
// Active / inactive is `statusCode` (ACTIVE / INACTIVE); there is no isActive.

export type OrganisationKind = 'entities' | 'business-units' | 'financial-years';

export type ProjectionStatusCode = 'ACTIVE' | 'INACTIVE';

export const PROJECTION_STATUS_OPTIONS: { value: ProjectionStatusCode; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export interface OrganisationTypeLookup {
  id: string;
  typeCode: string;
  typeName: string;
  description?: string | null;
  sortOrder: number;
}

export interface OrganisationLookups {
  entityTypes: OrganisationTypeLookup[];
  businessUnitTypes: OrganisationTypeLookup[];
}

// ─── Projection rows ──────────────────────────────────────────────────────────

export interface ProjectedEntity {
  id: string;
  canonicalId?: string | null;
  entityCode: string;
  entityName: string;
  entityTypeId?: string | null;
  entityTypeCode?: string | null;
  taxNumber?: string | null;
  registrationNumber?: string | null;
  statusCode?: ProjectionStatusCode | string | null;
  rowVersion: number;
  updatedAt?: string | null;
}

export interface ProjectedBusinessUnit {
  id: string;
  canonicalId?: string | null;
  entityId: string;
  entityCanonicalId?: string | null;
  entityCode?: string | null;
  parentUnitId?: string | null;
  parentCanonicalId?: string | null;
  parentUnitCode?: string | null;
  unitCode: string;
  unitName: string;
  unitTypeId: string;
  unitTypeCode?: string | null;
  statusCode?: ProjectionStatusCode | string | null;
  rowVersion: number;
  updatedAt?: string | null;
}

export interface ProjectedFinancialYear {
  id: string;
  canonicalId?: string | null;
  /** Null for a tenant-level year, as the master defines them. */
  entityId?: string | null;
  fyCode: string;
  fyName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isClosed: boolean;
  statusCode?: ProjectionStatusCode | string | null;
  rowVersion: number;
  updatedAt?: string | null;
}

// ─── Commands (write-through to the master) ───────────────────────────────────

export interface CreateEntityCommand {
  entityCode: string;
  entityName: string;
  entityTypeCode: string;
  taxNumber?: string | null;
  registrationNumber?: string | null;
}

export interface UpdateEntityCommand {
  entityName?: string | null;
  statusCode?: ProjectionStatusCode | null;
}

export interface CreateBusinessUnitCommand {
  /** Local entity id. */
  entityId: string;
  /** Local parent unit id. */
  parentUnitId?: string | null;
  unitCode: string;
  unitName: string;
  unitTypeCode: string;
}

export interface UpdateBusinessUnitCommand {
  unitName?: string | null;
  parentUnitId?: string | null;
  clearParent?: boolean;
  statusCode?: ProjectionStatusCode | null;
}

export interface CreateFinancialYearCommand {
  fyCode: string;
  fyName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
}

export interface UpdateFinancialYearCommand {
  fyName?: string | null;
  statusCode?: ProjectionStatusCode | null;
}

// ─── Lists, sync, verify ──────────────────────────────────────────────────────

/** Query of the paged lists (api OrganisationListRequest). pageSize ≤ 200. */
export interface OrganisationListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  /** Business units only (CRS, 2026-10-22): one unit type code, e.g. DEPARTMENT. */
  unitTypeCode?: string;
}

export interface OrganisationSyncResult {
  masterVersion: number;
  previousVersion: number;
  skipped: boolean;
  skipReason?: string | null;
  inserted: number;
  updated: number;
  unchanged: number;
  syncedAt: string;
}

export interface OrganisationDifference {
  kind: string;
  canonicalId: string;
  code: string;
  field: string;
  master?: string | null;
  local?: string | null;
}

export interface OrganisationVerifyResult {
  masterVersion: number;
  projectionVersion: number;
  lastSyncedAt?: string | null;
  missingLocally: OrganisationDifference[];
  mismatched: OrganisationDifference[];
  localOnly: OrganisationDifference[];
  orphanOrgRows: number;
  ok: boolean;
}
